import type { CadernoKind, CreateCadernoPayload, SubjectScope } from "../lib/caderno";
import type { TrilhaMode } from "./progress";
import { apiFetch } from "./client";
import { storedTopicQuiz, storeTopicQuiz } from "./topic-quiz";

// What POST/GET /api/lume/caderno/ returns — CadernoService::toArrayWithChildren
export interface CadernoSubjectResponse {
  id: number;
  subjectId: number | null;
  name: string | null;
  scope: SubjectScope;
  temaIds: number[];
}

export interface CadernoResponse {
  id: number;
  kind: CadernoKind;
  yearId: number;
  /** The school year's readable name (e.g. "ENEM"), resolved server-side from yearId */
  yearName: string | null;
  title: string;
  examDate: string | null;
  status: "active" | "archived" | "completed";
  createdAt: string;
  /** Which trilha the student follows — normal (Completo) or essential (O que mais sai) */
  trilhaMode: TrilhaMode;
  subjects: CadernoSubjectResponse[];
}

export async function createCaderno(
  payload: CreateCadernoPayload
): Promise<CadernoResponse> {
  const created = await apiFetch<CadernoResponse>("/api/lume/caderno/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, topicQuiz: storedTopicQuiz() }),
  });
  storeTopicQuiz(null);

  return created;
}

export function fetchCaderno(id: number): Promise<CadernoResponse> {
  return apiFetch<CadernoResponse>(`/api/lume/caderno/?id=${id}`);
}

export interface CadernoListResponse {
  items: CadernoResponse[];
  /**
   * The page actually served — the API clamps a page that is out of range
   * rather than failing, so this can differ from what was asked for.
   */
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/** One page of the logged-in student's active cadernos, newest first. */
export function fetchCadernos(page: number): Promise<CadernoListResponse> {
  return apiFetch<CadernoListResponse>(`/api/lume/caderno/?page=${page}`);
}

/**
 * Persist which trilha the student follows. Stored on the caderno so every server
 * surface (trilha, chat MCP tools, next-step) reads the same mode.
 */
export function setTrilhaMode(
  id: number,
  trilhaMode: TrilhaMode
): Promise<{ id: number; trilhaMode: TrilhaMode }> {
  return apiFetch<{ id: number; trilhaMode: TrilhaMode }>(
    `/api/lume/caderno/?id=${id}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ trilhaMode }),
    }
  );
}
